import React from "react";
import { ArrowRight } from "lucide-react";
import { useQuote } from "../context/QuoteContext";

// Served from the asset CDN (bypasses the Cloudflare Worker that routes .webp to WordPress).
// Source photos are stored rotated 90deg, so they are corrected via CSS.
const CDN = "https://customer-assets-m6fa6gv7.emergentagent.net/job_home-cinema-build/artifacts";

const projects = [
  {
    id: "fireplace-feature-wall",
    title: "Fireplace Feature Wall TV Mount",
    location: "Rocky Mount, NC",
    desc: "Recessed TV mounting on a tiled fireplace feature wall with fully concealed cables for a clean, built-in look.",
    before: `${CDN}/n4t8l417_20260729_113007.webp`,
    after: `${CDN}/gy6x7ica_20260729_124624.webp`,
  },
  {
    id: "bedroom-tv-mount",
    title: "Bedroom TV Wall Mount",
    location: "Rocky Mount, NC",
    desc: "Sleek wall-mounted TV above the dresser with tidy cable management — no more clutter, just a floating screen.",
    before: `${CDN}/3zslobzq_20260729_125336.webp`,
    after: `${CDN}/zpy0jk8t_20260729_132600.webp`,
  },
  {
    id: "family-room-tv-mount",
    title: "Family Room TV Mount",
    location: "Rocky Mount, NC",
    desc: "A large flat-screen mounted above the built-in cabinets, wired and ready to stream — a clean, functional focal point.",
    before: `${CDN}/de59xyjd_20260729_140506.webp`,
    after: `${CDN}/i0kxdqox_20260729_142034.webp`,
  },
];

const Frame = ({ src, label, alt }) => (
  <div className="relative flex-1 aspect-square overflow-hidden rounded-xl bg-slate-100">
    <img
      src={src}
      alt={alt}
      loading="lazy"
      className="w-full h-full object-cover [transform:rotate(90deg)]"
    />
    <span
      className={`absolute top-3 left-3 z-10 text-xs font-bold px-3 py-1 rounded-full backdrop-blur ${
        label === "Before"
          ? "bg-white/85 text-[#0a1f44]"
          : "bg-[#3b9dff] text-white"
      }`}
    >
      {label}
    </span>
  </div>
);

const BeforeAfter = () => {
  const { openQuote } = useQuote();
  return (
    <section className="section-pad bg-[#f5f8ff]" data-testid="before-after-section">
      <div className="max-w-[1280px] mx-auto px-5 lg:px-8">
        <div className="text-center mb-12">
          <span className="eyebrow">Real installations</span>
          <h2 className="text-3xl md:text-4xl font-extrabold text-[#0a1f44] mt-3">
            Before &amp; After
          </h2>
          <p className="text-slate-500 mt-3 max-w-2xl mx-auto">
            See the difference a professional install makes. Every job is finished
            with hidden cables and a clean, modern look.
          </p>
        </div>

        <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-6">
          {projects.map((p) => (
            <div
              key={p.id}
              data-testid={`project-${p.id}`}
              className="bg-white rounded-2xl border border-slate-100 shadow-sm hover:shadow-lg transition-shadow p-5"
            >
              <div className="flex gap-3">
                <Frame src={p.before} label="Before" alt={`${p.title} before installation in ${p.location}`} />
                <Frame src={p.after} label="After" alt={`${p.title} after professional installation in ${p.location}`} />
              </div>
              <div className="mt-5">
                <h3 className="text-xl font-bold text-[#0a1f44]">{p.title}</h3>
                <p className="text-[#3b9dff] text-sm font-semibold mt-1">{p.location}</p>
                <p className="text-slate-500 text-sm leading-relaxed mt-2">{p.desc}</p>
              </div>
            </div>
          ))}
        </div>

        <div className="text-center mt-12">
          <button
            onClick={() => openQuote()}
            data-testid="before-after-cta"
            className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-[#3b9dff] text-white font-semibold hover:bg-[#2b8ae8] transition-colors shadow-sm"
          >
            Get your free quote <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    </section>
  );
};

export default BeforeAfter;
